/**
 * Product Service
 * Thin adapter over lib/supabase helpers. Exposes the same function names
 * that pages import so pages require no changes in this phase.
 *
 * See docs/superpowers/plans/2026-06-10-storefront-supabase-catalog.md Task 1.
 */

import { supabase, supabaseHelpers } from '../lib/supabase';

// ---------------------------------------------------------------------------
// Catalog reads — delegate to real-schema helpers
// ---------------------------------------------------------------------------

/**
 * Fetch all active products.
 * Used with React Query key ['products'] in Products.js.
 */
export const fetchProducts = async () => {
  return supabaseHelpers.getProducts();
};

/**
 * Fetch the products flagged for the home page's featured section.
 * Used with React Query key ['featured-products'] in Home.js.
 */
export const fetchFeaturedProducts = async (limit = 3) => {
  return supabaseHelpers.getFeaturedProducts(limit);
};

/**
 * Fetch a single product by UUID or slug.
 * Used with React Query in ProductDetail.js.
 * Throws 'Product not found' so React Query's error state works.
 */
export const fetchProductById = async (idOrSlug) => {
  const product = await supabaseHelpers.getProduct(idOrSlug);
  if (!product) throw new Error('Product not found');
  return product;
};

// Alias kept for any direct slug lookups.
export const fetchProductBySlug = fetchProductById;

/**
 * Fetch active products filtered by category.
 * Used with React Query in CategoryProducts.js.
 *
 * The URL param is a lowercase slug (e.g. "wallets") while the DB stores
 * display-cased values (e.g. "Wallets"), and Postgres array contains() is
 * case-sensitive — so filter case-insensitively over the full catalog.
 */
export const fetchProductsByCategory = async (category) => {
  const products = await supabaseHelpers.getProducts();
  const normalized = category.toLowerCase().replace(/-/g, ' ');
  return products.filter((p) =>
    (p.categories || []).some((c) => c.toLowerCase() === normalized)
  );
};

/**
 * Search active products by name.
 */
export const searchProducts = async (query) => {
  return supabaseHelpers.searchProducts(query);
};

/**
 * Fetch display-only variations (option groups + values) for a product.
 * New export — used by the display-only ProductOptions component.
 */
export const fetchProductVariations = async (productId) => {
  return supabaseHelpers.getProductVariations(productId);
};

// ---------------------------------------------------------------------------
// Utilities — kept as-is
// ---------------------------------------------------------------------------

/**
 * Format currency for display.
 */
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

/**
 * Get product image URL with fallback to placeholder.
 */
export const getProductImageUrl = (image) => {
  if (!image) {
    return 'https://via.placeholder.com/600x400?text=No+Image+Available';
  }
  if (typeof image === 'object' && image.url) {
    return image.url;
  }
  if (typeof image === 'string') {
    if (image.startsWith('http') || image.startsWith('/')) {
      return image;
    }
  }
  return 'https://via.placeholder.com/600x400?text=No+Image+Available';
};

/**
 * Generate SEO-friendly product URL from slug or id.
 * Long Etsy-style slugs (>60 chars) are shortened to keep URLs clean.
 */
const generateConciseSlug = (name, category = '') => {
  let slug = category ? `${category.toLowerCase()}-` : '';
  const simpleName = name.split(/[,:/-]/)[0].trim();
  slug += simpleName
    .toLowerCase()
    .replace(/[^a-z0-9\s -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 60);
  return slug;
};

export const getProductUrl = (product) => {
  if (!product) return '';
  if (product.slug && product.slug.length > 60) {
    const category =
      product.categories && product.categories.length > 0 ? product.categories[0] : '';
    const conciseSlug = generateConciseSlug(product.name, category);
    product._conciseSlug = conciseSlug;
    return `/products/${conciseSlug}`;
  }
  return `/products/${product._conciseSlug || product.slug || product.id}`;
};

/**
 * Format tags for display (underscores → spaces).
 */
export const formatProductTags = (tags) => {
  if (!tags || !Array.isArray(tags)) return [];
  return tags.map((tag) => tag.replace(/_/g, ' '));
};

/**
 * Get filtered and sorted products (client-side, used in search/filter UI).
 */
export const getFilteredProducts = (products, filters = {}, sortBy = 'name', sortAsc = true) => {
  let filteredProducts = [...products];

  if (filters.category) {
    filteredProducts = filteredProducts.filter((product) =>
      product.category === filters.category
    );
  }
  if (filters.minPrice !== undefined) {
    filteredProducts = filteredProducts.filter((p) => p.price >= filters.minPrice);
  }
  if (filters.maxPrice !== undefined) {
    filteredProducts = filteredProducts.filter((p) => p.price <= filters.maxPrice);
  }
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filteredProducts = filteredProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(searchLower) ||
        (p.short_description && p.short_description.toLowerCase().includes(searchLower)) ||
        (p.long_description && p.long_description.toLowerCase().includes(searchLower))
    );
  }

  filteredProducts.sort((a, b) => {
    let valueA = a[sortBy];
    let valueB = b[sortBy];
    if (sortBy.includes('.')) {
      const parts = sortBy.split('.');
      valueA = parts.reduce((obj, key) => obj && obj[key], a);
      valueB = parts.reduce((obj, key) => obj && obj[key], b);
    }
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return sortAsc ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    }
    return sortAsc ? valueA - valueB : valueB - valueA;
  });

  return filteredProducts;
};

// ---------------------------------------------------------------------------
// Deprecated stubs — imported by pages/utils but no longer implemented.
// These throw on call so build stays green and misuse is loud.
// They will be removed when the importing pages are refactored (Task 2+).
// ---------------------------------------------------------------------------

/**
 * @deprecated Price calculation is no longer done client-side via this path.
 * ProductDetail.js imports this but does not call it; productCalculationUtils.js
 * wraps it in a try/catch. Safe to keep as a throwing stub.
 */
export const calculateProductWithOptions = async (_productId, _selectedOptions) => {
  throw new Error('calculateProductWithOptions: not implemented in Supabase catalog phase');
};

/**
 * @deprecated Product options are now fetched via fetchProductVariations.
 * Kept to avoid breaking any flagged-off page that imports it.
 */
export const fetchProductOptions = async () => {
  const { data, error } = await supabase.from('product_options').select('*');
  if (error) throw error;
  return data;
};

/**
 * @deprecated Use fetchProductVariations instead.
 */
export const fetchProductOptionById = async (id) => {
  const { data, error } = await supabase
    .from('product_options')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
};

/**
 * @deprecated Use fetchProductVariations instead.
 */
export const fetchProductOptionsForProduct = async (productId) => {
  const { data, error } = await supabase
    .from('product_option_links')
    .select('*')
    .eq('product_id', productId);
  if (error) throw error;
  return data;
};

/**
 * @deprecated Shipping calculation is not yet implemented in Supabase phase.
 */
export const calculateShippingRates = (_cartItems, _destination, _cartTotal) => {
  return [];
};
